import { CreditCard, ExternalLink, KeyRound, LogOut, Menu, Moon, Repeat, Settings, Sun, UserCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/use-auth';

function initials(name: string) {
    return name
        .split(' ')
        .map((part) => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
}

export function Topbar({ onMenuClick }: { onMenuClick?: () => void }) {
    const { user, tenant, logout } = useAuth();
    const navigate = useNavigate();
    const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));
    const canSeeBilling = !!user?.is_tenant_admin || !!user?.is_platform_admin;
    const canSeeSettings = !!user?.is_tenant_admin || !!user?.is_platform_admin;

    useEffect(() => {
        document.documentElement.classList.toggle('dark', isDark);
        localStorage.setItem('aipa-theme', isDark ? 'dark' : 'light');
    }, [isDark]);

    return (
        <header className="flex h-14 shrink-0 items-center justify-between gap-2 overflow-hidden border-b bg-card px-2 sm:px-4">
            <div className="flex min-w-0 items-center gap-1 sm:gap-3">
                <Button variant="ghost" size="icon" className="shrink-0 md:hidden" onClick={onMenuClick} aria-label="Open menu">
                    <Menu className="size-5" />
                </Button>

                {tenant ? (
                    <div className="flex min-w-0 items-center gap-3">
                        {tenant.industry_name && <span className="hidden truncate text-sm font-bold text-foreground sm:inline">{tenant.industry_name}</span>}
                        {tenant.industry_name && <div className="hidden h-4 w-px shrink-0 bg-border sm:block" />}
                        <span className="truncate text-sm font-bold text-foreground">{tenant.name}</span>
                    </div>
                ) : (
                    <div />
                )}
            </div>

            <div className="flex shrink-0 items-center gap-0.5 sm:gap-2">
                <Button variant="ghost" size="sm" asChild className="hidden lg:inline-flex">
                    <a href="/" target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="size-4" /> Visit website
                    </a>
                </Button>
                <Button variant="ghost" size="icon" asChild className="lg:hidden" aria-label="Visit website">
                    <a href="/" target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="size-4" />
                    </a>
                </Button>

                <Button variant="ghost" size="icon" onClick={() => setIsDark((v) => !v)} aria-label="Toggle theme">
                    {isDark ? <Sun className="size-4" /> : <Moon className="size-4" />}
                </Button>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button className="flex max-w-[10rem] items-center gap-2 rounded-md px-1.5 py-1 hover:bg-accent sm:max-w-none sm:px-2">
                            <Avatar className="size-7 shrink-0">
                                <AvatarImage src={user?.avatar_url ?? undefined} alt={user?.name} />
                                <AvatarFallback>{user ? initials(user.name) : '?'}</AvatarFallback>
                            </Avatar>
                            <span className="hidden truncate text-sm font-medium sm:inline">{user?.name}</span>
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate('/profile')}>
                            <UserCircle className="mr-2 size-4" />
                            Profile
                        </DropdownMenuItem>
                        {canSeeSettings && (
                            <DropdownMenuItem onClick={() => navigate('/settings')}>
                                <Settings className="mr-2 size-4" />
                                Settings
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => navigate('/change-password')}>
                            <KeyRound className="mr-2 size-4" />
                            Change Password
                        </DropdownMenuItem>
                        {canSeeBilling && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => navigate('/billing')}>
                                    <CreditCard className="mr-2 size-4" />
                                    Billing
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate('/subscription')}>
                                    <Repeat className="mr-2 size-4" />
                                    Subscription
                                </DropdownMenuItem>
                            </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => void logout()}>
                            <LogOut className="mr-2 size-4" />
                            Sign out
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </header>
    );
}
